import configFile from '@/../app-config'
import { AppHolder } from '@/utils/app-holder'
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import reflect from '../utils/reflect-promise'

import accounts from './modules/accounts'
import advice from './modules/advice'
import advisor from './modules/advisor'
import advisorsList from './modules/advisors-list'
import advisorArbitration from './modules/advisory-arbitration'
import arbitration from './modules/arbitration'
import asyncResults from './modules/async-results'
import auth from './modules/auth'
import banks from './modules/banks'
import bi from './modules/budget-insight'
import club from './modules/club'
import contracts from './modules/contracts'
import contractsCards from './modules/contracts-cards'
import customers from './modules/customers'
import error from './modules/error'
import funds from './modules/funds'
import interactions from './modules/interactions'
import investments from './modules/investments'
import loans from './modules/loans'
import missions from './modules/missions'
import multiArbitrationFunds from './modules/multi-arbitration-funds'
import notifications from './modules/notifications'
import portfolioBuilder from './modules/portfolio-builder'
import productCustomerArbitrations from './modules/product-customer-arbitrations'
import productTypes from './modules/product-types'
import productPortfolioBuilder from './modules/product-portfolio-builder'
import modelPortfolio from './modules/model-portfolio'
import collectiveArbitrations from './modules/collective-arbitrations'
import products from './modules/products'
import realEstates from './modules/real-estates'
import series from './modules/series'
import theme from './modules/theme'
import toasts from './modules/toasts'
import user from './modules/user'
import ux from './modules/ux'
import window from './modules/window'
import riskQuestionnaire from './modules/risk-questionnaire'
import esgQuestionnaire from './modules/esg-questionnaire'

import {
  CLOSE_WELCOME_CLUB,
  RESET_MODULE,
  START_APP_LOADING,
  STOP_APP_LOADING
} from '@/store/mutation-types'

Vue.use(Vuex)

const store = new Vuex.Store({
  namespaced: true,

  state: {
    notProd: process.env.NODE_ENV !== 'production',
    brandname: configFile[process.env.APP_ID].params.appId,
    brandnameAdvisory: `${configFile[process.env.APP_ID].params.appId}-advisory`,
    brandnamePdf: `${configFile[process.env.APP_ID].params.appId}-pdf-first`,
    secondBrandnamePdf: `${configFile[process.env.APP_ID].params.appId}-pdf-second`,
    patchnoteOpened: false,
    sidebarMode: null,
    privateMode: false,
    privateModeAdvisory: false,
    cookieAccepted: false,
    openedIntros: {},
    appCfg: configFile[process.env.APP_ID].params,
    appLoading: false,
    welcomeClub: true
  },

  actions: {
    startAppLoading ({ commit }) {
      commit(START_APP_LOADING)
    },

    stopAppLoading ({ commit }) {
      commit(STOP_APP_LOADING)
    },

    fetchAuthenticated ({ dispatch }, cached = true) {
      return dispatch('auth/fetchUser', cached)
        .then((user) => Promise.all([
          dispatch('accounts/fetch', { cached }),
          dispatch('realEstates/fetch', cached),
          dispatch('series/fetch', cached),
          dispatch('notifications/fetch', { cached }),
          dispatch('club/fetchClubSubscription')
        ]).then(() => user))
    },

    fetchAdvisory ({ dispatch }, cached = true) {
      return dispatch('advisor/fetchUser', cached)
        .then(() => Promise.all([
          reflect(dispatch('notifications/fetch', { cached }))
        ]))
        .catch(() => {
          dispatch('auth/logOut')
        })
    },

    fetchCustomerView ({ dispatch }, id, cached = true) {
      return Promise.all([
        dispatch('customers/fetchUser', {id, cached})
          .then(() => dispatch('accounts/fetch', {id: id, cached: false})),
        dispatch('series/fetch', cached)
      ])
    },

    closeWelcomeClub ({ commit }) {
      commit(CLOSE_WELCOME_CLUB)
    },

    reset ({ commit }) {
      commit(`advisorsList/${RESET_MODULE}`)
      commit('auth/resetState')
      commit('user/resetState')
      commit(`accounts/${RESET_MODULE}`)
      commit('bi/resetState')
      commit(`contracts/${RESET_MODULE}`)
      commit(`loans/${RESET_MODULE}`)
      commit(`realEstates/${RESET_MODULE}`)
      commit('contractsCards/resetState')
      commit('series/resetState')
      commit('funds/resetState')
      commit(`banks/${RESET_MODULE}`)
      commit('investments/resetState')
      commit('toasts/resetState')
      commit('notifications/resetState')
      commit('advisor/resetState')
      commit(`customers/${RESET_MODULE}`)
      commit(`advisorArbitration/${RESET_MODULE}`)
      commit(`interactions/${RESET_MODULE}`)
      commit(`missions/${RESET_MODULE}`)
      commit(`club/${RESET_MODULE}`)
      commit(`asyncResults/${RESET_MODULE}`)
      commit(`portfolioBuilder/${RESET_MODULE}`)
      commit(`productPortfolioBuilder/${RESET_MODULE}`)
      commit(`modelPortfolio/${RESET_MODULE}`)
      commit(`collectiveArbitrations/${RESET_MODULE}`)
      commit(`riskQuestionnaire/${RESET_MODULE}`)
      commit(`esgQuestionnaire/${RESET_MODULE}`)
    },

    resetCustomer ({ commit }) {
      commit(`accounts/${RESET_MODULE}`)
      commit(`contracts/${RESET_MODULE}`)
      commit(`loans/${RESET_MODULE}`)
      commit(`realEstates/${RESET_MODULE}`)
      commit('user/resetState')
    }
  },

  modules: {
    advisorsList: advisorsList(),
    auth: auth(),
    user: user(),
    accounts: accounts(),
    bi: bi(),
    contracts: contracts(),
    loans: loans(),
    realEstates: realEstates(),
    contractsCards: contractsCards(),
    series: series(),
    funds: funds(),
    banks: banks(),
    investments: investments(),
    error: error(),
    toasts: toasts(),
    notifications: notifications(),
    advisor: advisor(),
    advisorArbitration: advisorArbitration(),
    customers: customers(),
    interactions: interactions(),
    theme: theme(),
    window: window(),
    missions: missions(),
    club: club(),
    ux: ux(),
    advice: advice(),
    arbitration: arbitration(),
    asyncResults: asyncResults(),
    products: products(),
    productCustomerArbitrations: productCustomerArbitrations(),
    productTypes: productTypes(),
    portfolioBuilder: portfolioBuilder(),
    productPortfolioBuilder: productPortfolioBuilder(),
    modelPortfolio: modelPortfolio(),
    multiArbitrationFunds: multiArbitrationFunds(),
    collectiveArbitrations: collectiveArbitrations(),
    riskQuestionnaire: riskQuestionnaire(),
    esgQuestionnaire: esgQuestionnaire()
  },

  mutations: {
    [START_APP_LOADING] (state, data) {
      state.appLoading = true
    },

    [STOP_APP_LOADING] (state, data) {
      state.appLoading = false
    },

    [CLOSE_WELCOME_CLUB] (state) {
      state.welcomeClub = false
    },

    patchnoteOpened (state, data) {
      state.patchnoteOpened = data
    },
    setSidebarMode (state, data) {
      state.sidebarMode = data
    },
    switchPrivateMode (state) {
      state.privateMode = !state.privateMode
    },
    switchPrivateModeAdvisory (state) {
      const newValue = !state.privateModeAdvisory
      state.privateModeAdvisory = newValue
      state.privateMode = newValue
    },
    acceptCookie (state) {
      state.cookieAccepted = true
    },
    setOpenedIntro (state, stepId) {
      state.openedIntros = {
        ...state.openedIntros,
        [stepId]: true
      }
    }
  },

  plugins: [
    createPersistedState({
      paths: [
        'user.aggregationHistory',
        'user.authenticated',
        'patchnoteOpened',
        'sidebarMode',
        'advisor.authenticated',
        'theme',
        'privateMode',
        'privateModeAdvisory',
        'cookieAccepted',
        'openedIntros',
        'welcomeClub',
        'auth.redirectRoute',
        'auth.accessToken',
        'auth.refreshToken',
        'auth.hasRole',
        'bi.BIToken'
      ]
    })
  ]
})

AppHolder.store = store

export default store
